import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import style from './ui-section-header.css?inline';
import '../layout/ui-layout-bar/ui-layout-bar';
import '../ui-icons/ui-chevron-arrow-icon';

const CName = 'ui-section-header';

export interface UISectionHeaderAction {
  text: string;
  ariaLabel?: string;
  callback: () => void;
}

@customElement(CName)
export class UISectionHeader extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property({ type: String }) class?: string;
  @property({ type: Object }) action?: UISectionHeaderAction;
  @property({ type: String }) description?: string;
  @property({ type: String }) href?: string;
  @property({ type: String }) iconUrl?: string;
  @property({ type: String }) title = '';
  @property({ type: Number }) order?: number;
  @property({ type: Boolean }) enLangAttr?: boolean;

  render() {
    const action = this.action?.text
      ? this.href
        ? html`
          <a
            href=${this.href}
            aria-label=${this.action?.ariaLabel ?? nothing}
            @click=${(ev: PointerEvent) => ev.preventDefault()}
            @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._onActionClick.bind(this))}
          >
            ${this._renderAction()}
          </a>
        `
        : html`<button class="action__button" aria-label=${this.action?.ariaLabel ?? nothing} @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._onActionClick.bind(this))}>${this._renderAction()}</button>`
      : nothing;

    return html`
      <ui-layout-bar .class="${this.class}">
        <div slot="left" class="wrapper">${this._renderIcon()} ${this._renderTitle()}</div>
        <div slot="right" class="fixed-height">${action}</div>
      </ui-layout-bar>
    `;
  }

  private _renderIcon() {
    if (!this.iconUrl) return nothing;

    return html`
      <div class="icon">
        <img src=${this.iconUrl} alt=${window.$app.$t.get(I18n.a11y.image.sectionIcon, { title: this.title })} />
      </div>
    `;
  }

  private _renderTitle() {
    return html`
      <div class="title">
        ${
          this.order === 1
            ? html`<h1 lang=${this.enLangAttr ? 'en' : nothing}>${this.title}</h1>`
            : html`<h2 lang=${this.enLangAttr ? 'en' : nothing}>${this.title}</h2>`
        }
        ${this._renderDescription()}
      </div>
    `;
  }

  private _renderDescription() {
    if (!this.description) return nothing;

    return html`<p>${this.description}</p>`;
  }

  private _renderAction() {
    if (!this.action?.text) return nothing;

    return html`
      <div class="action" @click="${this._onActionClick}">
        <span>${this.action.text}</span>
        <ui-chevron-arrow-icon class="action__icon"></ui-chevron-arrow-icon>
      </div>
    `;
  }

  private _onActionClick() {
    if (this.action?.callback) {
      return this.action.callback();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UISectionHeader;
  }
}
